﻿import React, { useState, useEffect, useRef } from 'react'
import { PermissionGuard } from '../components/PermissionGuard'
import { Heading, Text, Button, Input, Select, LoadingSpinner, Tooltip } from '../components/ui'
import { 
  listArticles, 
  listCategories,
  listDepartments,
  listTags,
  getMultipleUsersInfo,
  addFavorite,
  removeFavorite,
  getFavoriteArticleIds,
  listFavorites,
  trackArticleView,
  getPublishedWikiStats
} from '../services/wiki'
import { WikiArticle, WikiCategory, WikiDepartment, WikiTag } from '../types/wiki'
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  TagIcon,
  FolderIcon,
  CalendarIcon,
  UserIcon,
  XMarkIcon,
  EyeIcon,
  FunnelIcon,
  XCircleIcon,
  HeartIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import {
  HeartIcon as HeartIconSolid
} from '@heroicons/react/24/solid'

const WikiBiblioteca: React.FC = () => {
  // Estados principais
  const [articles, setArticles] = useState<WikiArticle[]>([])
  const [categories, setCategories] = useState<WikiCategory[]>([])
  const [departments, setDepartments] = useState<WikiDepartment[]>([])
  const [tags, setTags] = useState<WikiTag[]>([])
  const [authors, setAuthors] = useState<Array<{id: string, name: string}>>([])
  const [userNames, setUserNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Estados de filtros e busca
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [selectedAuthor, setSelectedAuthor] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Estados de modais
  const [showViewModal, setShowViewModal] = useState(false)
  const [isViewMaximized, setIsViewMaximized] = useState(false)

  // Estados de artigos
  const [viewingArticle, setViewingArticle] = useState<WikiArticle | null>(null)

  // Estados de favoritos
  const [favoriteIds, setFavoriteIds] = useState<string[]>([])
  const [favorites, setFavorites] = useState<WikiArticle[]>([])
  const [showFavorites, setShowFavorites] = useState(false)

  // Estados de estatísticas (apenas artigos publicados)
  const [stats, setStats] = useState<{
    total_articles: number
    total_categories: number
    total_tags: number
  } | null>(null)

  // Estado para controlar visibilidade dos filtros
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  // Função para buscar dados - SEMPRE filtra por status='published'
  const fetchData = async (opts?: { showOverlay?: boolean, query?: string }) => {
    try {
      const showOverlay = opts?.showOverlay ?? true
      if (showOverlay) {
        setLoading(true)
      }
      setError(null)

      const [articlesData, categoriesData, departmentsData, tagsData, statsData] = await Promise.all([
        listArticles({
          query: opts?.query ?? searchTerm,
          category_id: selectedCategory,
          department_id: selectedDepartment,
          tag_ids: selectedTag ? [selectedTag] : undefined,
          created_by: selectedAuthor || undefined,
          status: 'published', // SEMPRE filtrar apenas artigos publicados
          sort_by: sortBy,
          sort_order: sortOrder
        }, currentPage, itemsPerPage),
        listCategories(),
        listDepartments(),
        listTags(),
        getPublishedWikiStats()
      ])

      setArticles(articlesData.articles)
      setTotalItems(articlesData.total)
      setTotalPages(Math.ceil(articlesData.total / itemsPerPage))
      setCategories(categoriesData)
      setDepartments(departmentsData)
      setTags(tagsData)
      setStats(statsData)

      // Buscar nomes dos usuários (autores)
      const authorIds = Array.from(new Set(articlesData.articles.map(article => article.created_by).filter(Boolean))) as string[]
      
      if (authorIds.length > 0) {
        const usersData = await getMultipleUsersInfo(authorIds)
        const userNamesMap: Record<string, string> = {}
        const authorsList: Array<{id: string, name: string}> = []
        
        Object.entries(usersData).forEach(([id, user]) => {
          userNamesMap[id] = user.name
          if (authorIds.includes(id)) {
            authorsList.push({ id, name: user.name })
          }
        })
        setUserNames(userNamesMap)
        setAuthors(authorsList.sort((a, b) => a.name.localeCompare(b.name)))
      }

      setRetryCount(0)
      // Atualizar favoritos após carregar artigos
      try {
        const ids = await getFavoriteArticleIds()
        setFavoriteIds(ids)
        const favs = await listFavorites()
        const favArticles = (favs || [])
          .map((f: any) => f.article)
          .filter(Boolean)
        setFavorites(favArticles)
      } catch (e) {
        console.error('Erro ao carregar favoritos:', e)
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
      setRetryCount(prev => prev + 1)
    } finally {
      setLoading(false)
    }
  }

  // Função de retry
  const retryFetch = () => {
    if (retryCount < 3) {
      fetchData()
    }
  }

  // Debounce do termo de busca
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400)
    return () => clearTimeout(handler)
  }, [searchTerm])

  // Resetar página para 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, selectedCategory, selectedDepartment, selectedTag, selectedAuthor, sortBy, sortOrder])

  // Evitar overlay durante mudanças de filtros
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      fetchData({ showOverlay: true, query: debouncedSearchTerm })
      return
    }
    fetchData({ showOverlay: false, query: debouncedSearchTerm })
  }, [debouncedSearchTerm, currentPage, itemsPerPage, selectedCategory, selectedDepartment, selectedTag, selectedAuthor, sortBy, sortOrder])

  const openViewModal = (article: WikiArticle) => {
    setViewingArticle(article)
    setShowViewModal(true)
    trackArticleView(article.id)
    setIsViewMaximized(false)
  }

  const closeViewModal = () => {
    setShowViewModal(false)
    setViewingArticle(null)
    setIsViewMaximized(false)
  }

  // Funções de favoritos
  const handleToggleFavorite = async (articleId: string) => {
    try {
      const isFav = favoriteIds.includes(articleId)
      if (isFav) {
        await removeFavorite(articleId)
      } else {
        await addFavorite(articleId)
      }
      
      const newFavoriteIds = await getFavoriteArticleIds()
      setFavoriteIds(newFavoriteIds)
    } catch (err) {
      console.error('Erro ao alterar favorito:', err)
    }
  }

  // Limpar filtros
  const handleClearFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSelectedCategory('')
    setSelectedDepartment('')
    setSelectedTag('')
    setSelectedAuthor('')
    setSortBy('created_at')
    setSortOrder('desc')
    setShowFavorites(false)
    setCurrentPage(1)
  }

  // Funções de paginação
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagesCount) {
      setCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  // Função para processar links internos
  const processInternalLinks = (content: string, articles: WikiArticle[]) => {
    return content.replace(/\[\[([^\]]+)\]\]/g, (match, title) => {
      const article = articles.find(a => a.title === title)
      if (article) {
        return `<a href="#" onclick="event.preventDefault(); window.openArticle('${article.id}')" class="text-primary-600 hover:text-primary-800 underline">${title}</a>`
      }
      return match
    })
  }

  // Artigos para exibição (com suporte ao filtro de favoritos)
  const displayArticles = showFavorites ? favorites : articles
  
  // Aplicar filtros de busca nos favoritos também
  const filteredDisplayArticles = showFavorites 
    ? displayArticles.filter(article => {
        if (!debouncedSearchTerm) return true
        const searchLower = debouncedSearchTerm.toLowerCase()
        return article.title.toLowerCase().includes(searchLower) || 
               article.body.toLowerCase().includes(searchLower)
      })
    : displayArticles
  
  // Determinar se há filtros aplicados
  const hasFilters = debouncedSearchTerm || selectedCategory || selectedDepartment || selectedTag || selectedAuthor || showFavorites
  
  // Calcular valores de paginação baseados nos resultados filtrados
  const resultsCount = hasFilters ? filteredDisplayArticles.length : totalItems
  const pagesCount = hasFilters ? Math.ceil(filteredDisplayArticles.length / itemsPerPage) : totalPages
  
  // Aplicar paginação aos artigos filtrados
  const paginatedArticles = hasFilters 
    ? filteredDisplayArticles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : articles
  
  const showingStart = resultsCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const showingEnd = Math.min(currentPage * itemsPerPage, resultsCount)

  // Ajustar página atual se ela for maior que o número total de páginas
  useEffect(() => {
    if (pagesCount > 0 && currentPage > pagesCount) {
      setCurrentPage(1)
    }
  }, [pagesCount, currentPage])

  return (
    <div className="h-full flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <Text size="lg" className="text-gray-600 dark:text-gray-400 mb-2">
                Carregando Biblioteca da Wiki...
              </Text>
              {retryCount > 0 && (
                <Text size="sm" className="text-gray-500 dark:text-gray-500">
                  Tentativa {retryCount} de 3...
                </Text>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <Text size="lg" className="text-red-600 dark:text-red-400 mb-2">
                Erro ao carregar Biblioteca da Wiki
              </Text>
              <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </Text>
              <Button
                onClick={retryFetch}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Tentar Novamente
              </Button>
              {retryCount > 0 && (
                <Text size="xs" className="text-gray-500 dark:text-gray-500 mt-2">
                  Já tentamos {retryCount} vezes
                </Text>
              )}
            </div>
          </div>
        ) : (
          <PermissionGuard 
            moduleName="wiki_biblioteca" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar a Biblioteca da Wiki.
                  </Text>
                </div>
              </div>
            }
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  Biblioteca da Wiki
                </Heading>
                {stats && (
                  <Text size="sm" color="muted" className="mt-2">
                    {stats.total_articles} artigos publicados • {stats.total_categories} categorias • {stats.total_tags} tags
                  </Text>
                )}
              </div>
            </div>

            {/* Filtros */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {/* Header dos filtros com botão de toggle */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => { setShowFavorites(!showFavorites); setCurrentPage(1) }}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                    aria-pressed={showFavorites}
                  >
                    {showFavorites ? (
                      <HeartIconSolid className="h-4 w-4 text-red-500" />
                    ) : (
                      <HeartIcon className="h-4 w-4" />
                    )}
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    <Text size="sm">Ordenar por:</Text>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-40"
                    >
                      <option value="created_at">Data</option>
                      <option value="title">Título</option>
                    </Select>
                    <Button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      variant="outline"
                      size="sm"
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </Button>
                    <Button
                      onClick={handleClearFilters}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Limpar filtros
                    </Button>
                  </div>
                </div>
                
                <Button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <FunnelIcon className="h-4 w-4" />
                  {filtersExpanded ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </Button>
              </div>

              {/* Filtros expandíveis */}
              <div className={`transition-all duration-300 ease-in-out ${
                filtersExpanded
                  ? 'max-h-96 opacity-100 mt-4 overflow-visible'
                  : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 py-0.5">
                  <div>
                    <Text size="sm" className="mb-2">Buscar</Text>
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar artigos..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Categoria</Text>
                    <Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="">Todas as categorias</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Departamento</Text>
                    <Select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                    >
                      <option value="">Todos os departamentos</option>
                      {departments.map(department => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Autor</Text>
                    <Select
                      value={selectedAuthor}
                      onChange={(e) => setSelectedAuthor(e.target.value)}
                    >
                      <option value="">Todos os autores</option>
                      {authors.map(author => (
                        <option key={author.id} value={author.id}>
                          {author.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Artigos */}
            <div className="flex-1 overflow-y-auto p-6">
              {paginatedArticles.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <Text size="lg" className="text-gray-500 mb-2">
                    Nenhum artigo publicado encontrado
                  </Text>
                  <Text size="sm" className="text-gray-400">
                    Tente ajustar os filtros de busca
                  </Text>
                </div>
              ) : (
                <div className="grid gap-4">
                  {paginatedArticles.map((article) => (
                    <div key={article.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                              {article.title}
                            </Heading>
                            <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              Publicado
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                            {article.category && (
                              <div className="flex items-center gap-1">
                                <FolderIcon className="h-4 w-4" />
                                {article.category.name}
                              </div>
                            )}
                            {article.department && (
                              <div className="flex items-center gap-1">
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                                  {article.department.name}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="h-4 w-4" />
                              {new Date(article.created_at).toLocaleDateString('pt-BR')}
                            </div>
                            <div className="flex items-center gap-1">
                              <UserIcon className="h-4 w-4" />
                              {userNames[article.created_by || ''] || article.created_by || 'Usuário'}
                            </div>
                          </div>
                          
                          {article.tags && article.tags.length > 0 && (
                            <div className="flex items-center gap-2 mb-3">
                              {article.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs"
                                >
                                  <TagIcon className="h-3 w-3" />
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="text-gray-600 dark:text-gray-300 line-clamp-2">
                            {article.body.replace(/<[^>]*>/g, '').substring(0, 200)}...
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <Tooltip content={favoriteIds.includes(article.id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}>
                            <Button
                              onClick={() => handleToggleFavorite(article.id)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              {favoriteIds.includes(article.id) ? (
                                <HeartIconSolid className="h-4 w-4 text-red-500" />
                              ) : (
                                <HeartIcon className="h-4 w-4" />
                              )}
                            </Button>
                          </Tooltip>
                          <Tooltip content="Visualizar artigo">
                            <Button
                              onClick={() => openViewModal(article)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Paginação */}
            {!loading && resultsCount > 0 && (
              <div className="mt-6 w-full">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-4">
                    <Text size="sm" className="text-gray-600 dark:text-gray-400">
                      {hasFilters ? (
                        <>Mostrando {showingStart} a {showingEnd} de {resultsCount} resultados filtrados</>
                      ) : (
                        <>Mostrando {showingStart} a {showingEnd} de {resultsCount} resultados</>
                      )}
                    </Text>
                    <Select
                      value={itemsPerPage.toString()}
                      onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                      className="w-20"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </Select>
                    <Text size="sm" className="text-gray-600 dark:text-gray-400">
                      por página
                    </Text>
                  </div>
                  
                  {pagesCount > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        size="sm"
                        variant="outline"
                      >
                        Anterior
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, pagesCount) }, (_, i) => {
                          const page = i + 1
                          return (
                            <Button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              size="sm"
                              variant={currentPage === page ? "solid" : "outline"}
                              className={currentPage === page ? "bg-primary-600 text-white" : ""}
                            >
                              {page}
                            </Button>
                          )
                        })}
                        {pagesCount > 5 && (
                          <>
                            <Text size="sm" className="text-gray-500">...</Text>
                            <Button
                              onClick={() => handlePageChange(pagesCount)}
                              size="sm"
                              variant={currentPage === pagesCount ? "solid" : "outline"}
                              className={currentPage === pagesCount ? "bg-primary-600 text-white" : ""}
                            >
                              {pagesCount}
                            </Button>
                          </>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === pagesCount}
                        size="sm"
                        variant="outline"
                      >
                        Próxima
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </PermissionGuard>
        )}

      {/* Modal de Visualização */}
      {showViewModal && viewingArticle && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex ${isViewMaximized ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-4'} z-50`}>
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${isViewMaximized ? 'max-w-none h-full' : 'max-w-4xl max-h-[90vh]'} flex flex-col`}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {viewingArticle.title}
              </Heading>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setIsViewMaximized(v => !v)}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {isViewMaximized ? 'Restaurar' : 'Maximizar'}
                </Button>
                <Button
                  onClick={closeViewModal}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Fechar
                </Button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Metadados */}
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 flex-wrap mb-6">
                {viewingArticle.category && (
                  <div className="flex items-center gap-1">
                    <FolderIcon className="h-4 w-4" />
                    {viewingArticle.category.name}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(viewingArticle.created_at).toLocaleDateString('pt-BR')}
                </div>
                <div className="flex items-center gap-1">
                  <UserIcon className="h-4 w-4" />
                  {userNames[viewingArticle.created_by || ''] || viewingArticle.created_by || 'Usuário'}
                </div>
                {viewingArticle.department && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                    {viewingArticle.department.name}
                  </span>
                )}
                <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Publicado
                </span>
                {viewingArticle.tags && viewingArticle.tags.length > 0 && (
                  <>
                    {viewingArticle.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs"
                      >
                        <TagIcon className="h-3 w-3" />
                        {tag.name}
                      </span>
                    ))}
                  </>
                )}
              </div>

              {/* Conteúdo do artigo */}
              <div 
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: processInternalLinks(viewingArticle.body, articles) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WikiBiblioteca
